import React, { useEffect, useRef, useState } from "react";
import { Pencil, Check, X } from "lucide-react";

export const MAX_NAME_LEN = 80;

/**
 * Shared inline quick rename for menu products and categories.
 * Backend-confirmed: the visible name only changes once the canonical save resolves, and the
 * canonical name from the response wins. Blur cancels (never silently saves) and an unchanged
 * name closes without a request.
 */
export function InlineRename({ value, onSave, testid, textClass = "", disabled, onEditingChange, isActive = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { onEditingChange?.(editing); }, [editing]); // eslint-disable-line
  // Another row started renaming: drop this unsaved edit (never auto-save).
  useEffect(() => {
    if (editing && !isActive && !saving) { setEditing(false); setDraft(value); }
  }, [isActive]); // eslint-disable-line
  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  const open = () => { setDraft(value); setEditing(true); };
  const cancel = () => { if (!saving) { setEditing(false); setDraft(value); } };
  const commit = async () => {
    if (saving) return;
    const next = draft.trim();
    if (!next || next.length > MAX_NAME_LEN) return;
    if (next === value) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok) setEditing(false);
    else { setEditing(false); setDraft(value); }
  };

  if (!editing) {
    return (
      <span className="flex items-center gap-1.5 min-w-0">
        <span className={`truncate ${textClass}`}>{value}</span>
        {!disabled && (
          <button data-testid={`quick-rename-${testid}`} aria-label="Brzo preimenuj"
            onClick={(e) => { e.stopPropagation(); open(); }}
            className="p-1 text-slate-300 hover:text-slate-700 shrink-0"><Pencil size={13} /></button>
        )}
      </span>
    );
  }
  const invalid = !draft.trim();
  return (
    <span className="flex items-center gap-1.5 min-w-0 flex-1" onPointerDown={(e) => e.stopPropagation()}>
      <input ref={inputRef} data-testid={`rename-input-${testid}`} value={draft} maxLength={MAX_NAME_LEN}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={cancel}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        className={`flex-1 min-w-0 rounded-lg border px-2 py-1.5 text-sm font-semibold outline-none ${invalid ? "border-brand-red" : "border-slate-300 focus:border-slate-800"}`} />
      <span className="text-[10px] font-bold text-slate-400 shrink-0 tabular-nums">{draft.trim().length}/{MAX_NAME_LEN}</span>
      <button data-testid={`rename-save-${testid}`} aria-label="Sačuvaj naziv" disabled={saving || invalid}
        onMouseDown={(e) => e.preventDefault()} onClick={commit}
        className="p-1.5 rounded-lg bg-brand-green/10 text-brand-green disabled:opacity-40 shrink-0"><Check size={15} /></button>
      <button data-testid={`rename-cancel-${testid}`} aria-label="Otkaži preimenovanje" disabled={saving}
        onMouseDown={(e) => e.preventDefault()} onClick={cancel}
        className="p-1.5 rounded-lg bg-slate-100 text-slate-500 disabled:opacity-40 shrink-0"><X size={15} /></button>
    </span>
  );
}
